/**
 * Client debug logger — hatalari backend'e gonderir + console'a yazar.
 * Offline veya endpoint basarisizsa sessizce gecer.
 */

const API = import.meta.env.VITE_API_URL || 'http://localhost:8000';
const MAX_QUEUE = 50;
const RATE_LIMIT_MS = 2000;

let lastSendAt = 0;
const recentMessages = new Map<string, number>();

function shouldSkip(message: string): boolean {
  const now = Date.now();
  // Ayni mesaj son 10 saniyede gonderildiyse skip (spam onleme)
  const last = recentMessages.get(message);
  if (last && now - last < 10000) return true;
  recentMessages.set(message, now);
  if (recentMessages.size > MAX_QUEUE) {
    const firstKey = recentMessages.keys().next().value;
    if (firstKey !== undefined) recentMessages.delete(firstKey);
  }
  return false;
}

export type LogLevel = 'error' | 'warn' | 'info';

interface LogPayload {
  level?: LogLevel;
  message: string;
  stack?: string;
  extra?: Record<string, unknown>;
}

export async function logToServer(p: LogPayload): Promise<void> {
  try {
    if (shouldSkip(p.message)) return;
    const now = Date.now();
    if (now - lastSendAt < RATE_LIMIT_MS) {
      await new Promise(r => setTimeout(r, RATE_LIMIT_MS - (now - lastSendAt)));
    }
    lastSendAt = Date.now();

    const token = localStorage.getItem('sudoku_token') || undefined;
    const body = {
      level: p.level || 'error',
      message: p.message.slice(0, 500),
      stack: p.stack?.slice(0, 3000),
      url: window.location.href,
      user_agent: navigator.userAgent,
      extra: p.extra,
    };
    await fetch(`${API}/debug/log`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
      keepalive: true,
    });
  } catch {
    /* sessizce gec */
  }
}

export function installGlobalErrorHandlers(): void {
  if (typeof window === 'undefined') return;

  window.addEventListener('error', (e: ErrorEvent) => {
    const msg = e.message || 'window.error';
    console.error('[client]', msg, e.error);
    logToServer({
      level: 'error',
      message: msg,
      stack: e.error?.stack,
      extra: { filename: e.filename, lineno: e.lineno, colno: e.colno },
    });
  });

  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    const reason = e.reason;
    const msg = reason?.message || String(reason) || 'unhandledrejection';
    console.error('[client] unhandled promise:', reason);
    logToServer({
      level: 'error',
      message: `unhandledrejection: ${msg}`.slice(0, 500),
      stack: reason?.stack,
    });
  });
}

// Kolay API
export const clientLog = {
  error: (message: string, extra?: Record<string, unknown>) =>
    logToServer({ level: 'error', message, extra }),
  warn: (message: string, extra?: Record<string, unknown>) =>
    logToServer({ level: 'warn', message, extra }),
  info: (message: string, extra?: Record<string, unknown>) =>
    logToServer({ level: 'info', message, extra }),
};
